import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';

@Component({
  selector: 'app-signature-pad',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './signature-pad.component.html',
  styleUrl: './signature-pad.component.scss',
})
export class SignaturePadComponent implements AfterViewInit, OnChanges {
  @ViewChild('canvasRef', { static: true })
  private readonly canvasRef!: ElementRef<HTMLCanvasElement>;

  @Input() initialSignature: string | null = null;

  @Output() signatureChange = new EventEmitter<string | null>();

  hasSignature = false;

  private drawing = false;

  private lastEmittedSignature: string | null = null;

  ngAfterViewInit(): void {
    this.setupCanvas();
    this.syncInitialSignature();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const signatureChange = changes['initialSignature'];

    if (!signatureChange) {
      return;
    }

    const nextSignature = signatureChange.currentValue as string | null;

    if (nextSignature && nextSignature === this.lastEmittedSignature) {
      return;
    }

    this.syncInitialSignature();
  }

  startDrawing(event: PointerEvent): void {
    const canvas = this.canvasRef.nativeElement;
    const context = canvas.getContext('2d');

    if (!context) return;

    canvas.setPointerCapture(event.pointerId);

    this.drawing = true;

    const point = this.getCanvasPoint(event);

    context.beginPath();
    context.moveTo(point.x, point.y);
  }

  draw(event: PointerEvent): void {
    if (!this.drawing) return;

    const canvas = this.canvasRef.nativeElement;
    const context = canvas.getContext('2d');

    if (!context) return;

    const point = this.getCanvasPoint(event);

    context.lineTo(point.x, point.y);
    context.stroke();

    this.hasSignature = true;
  }

  endDrawing(): void {
    if (this.drawing && this.hasSignature) {
      this.emitSignature();
    }

    this.drawing = false;
  }

  clear(): void {
    const canvas = this.canvasRef.nativeElement;
    const context = canvas.getContext('2d');

    if (!context) return;

    context.save();

    context.setTransform(1, 0, 0, 1, 0, 0);

    context.clearRect(0, 0, canvas.width, canvas.height);

    context.restore();

    this.hasSignature = false;
    this.lastEmittedSignature = null;
    this.signatureChange.emit(null);
  }

  private setupCanvas(): void {
    const canvas = this.canvasRef.nativeElement;
    const context = canvas.getContext('2d');

    if (!context) return;

    const ratio = window.devicePixelRatio || 1;

    const width = canvas.clientWidth;
    const height = canvas.clientHeight;

    canvas.width = width * ratio;
    canvas.height = height * ratio;

    context.scale(ratio, ratio);

    context.lineCap = 'round';
    context.lineJoin = 'round';

    context.strokeStyle = '#f0b54d';

    context.lineWidth = 2.6;
  }

  private getCanvasPoint(event: PointerEvent): { x: number; y: number } {
    const canvas = this.canvasRef.nativeElement;

    const rect = canvas.getBoundingClientRect();

    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
  }

  private emitSignature(): void {
    const canvas = this.canvasRef.nativeElement;

    const signature = canvas.toDataURL('image/png');

    this.lastEmittedSignature = signature;

    this.signatureChange.emit(signature);
  }

  private syncInitialSignature(): void {
    const signature = this.initialSignature;

    const canvas = this.canvasRef?.nativeElement;
    const context = canvas?.getContext('2d');

    if (!canvas || !context) return;

    context.clearRect(0, 0, canvas.width, canvas.height);

    if (!signature) {
      this.hasSignature = false;
      return;
    }

    const image = new Image();

    image.onload = () => {
      context.save();
      context.setTransform(1, 0, 0, 1, 0, 0);
      context.clearRect(0, 0, canvas.width, canvas.height);
      context.restore();
      context.drawImage(image, 0, 0, canvas.clientWidth, canvas.clientHeight);

      this.hasSignature = true;
    };

    image.src = signature;
  }
}
